import { useState, useEffect } from "react";
import CustomDropdown from "@/components/CustomDropdown";
import {
  useFetchAllCompanyJobGradesQuery,
  useFetchRegionsForLivePayQuery,
  useFetchCompanyJobFunctionsQuery,
  useBenchmarkingReportByTitleQuery,
} from "@/services";
import { useTypedSelector } from "@/hooks";
import { getSortedCountry, getSortedRegionOptions } from "@/utils";

function formatString(inputString) {
  const formattedString = {
    base_pay: "Base Pay",
    direct_cash: "Total Direct Compensation",
    guaranteed_cash: "Total Guaranteed Cash",
    target_cash: "Target Total Cash",
    total_remuneration: "Total Remuneration",
    total_direct_compensation: "Total Direct Compensation",
  };
  return formattedString[inputString] || inputString;
}

const formatNumberWithCommas = (number: any): string | null => {
  // Check if the number is null, undefined, or NaN, or if it's an empty string
  if (number == null || isNaN(Number(number)) || number === "") return null;

  // Convert input to a number if it's a string
  const numericValue = Number(number);

  // If the number is zero, return "0" explicitly
  if (numericValue === 0) return "";

  // Return the formatted number with commas using toLocaleString
  return numericValue.toLocaleString();
};

const BenchmarkingByTitle = () => {
  const companyId = useTypedSelector(state => state.auth?.user?.company?.id);

  const initialFormValues = {
    title: "",
    region: "",
    country: "",
    grade: "",
    jobFunction: "",
    subJobFunction: "",
    reportOn: "",
  };

  const [formValues, setFormValues] = useState(initialFormValues);
  const [submitData, setSubmitData] = useState(null);

  // API Queries
  const { data: livePayRegions } = useFetchRegionsForLivePayQuery({
    company_id: companyId,
  });
  const { data: jobGradesData } = useFetchAllCompanyJobGradesQuery({
    company_id: companyId,
    paginate: false,
  });
  const { data: companyJobFunctionsData } = useFetchCompanyJobFunctionsQuery({
    companyId,
    reportOn: formValues.reportOn,
  });

  const isValidSubmitData = submitData !== null;

  const {
    data: apiResponse,
    isLoading,
    error,
  } = useBenchmarkingReportByTitleQuery(
    isValidSubmitData ? { company_id: companyId, ...submitData } : null,
    { skip: !isValidSubmitData }
  );

  // ✅ Reset all fields when companyId changes
  useEffect(() => {
    setFormValues(initialFormValues);
    setSubmitData(null);
  }, [companyId]);

  // ✅ Auto-trigger API when all fields are filled
  useEffect(() => {
    const isValid =
      // formValues.title &&
      formValues.country &&
      formValues.grade &&
      formValues.jobFunction &&
      // formValues.subJobFunction &&
      formValues.reportOn;

    if (isValid) {
      const request = {
        title: formValues.title || null,
        country_id: Number(formValues.country),
        grade_id: Number(formValues.grade),
        job_function_id: Number(formValues.jobFunction),
        job_sub_function_id: formValues.subJobFunction
          ? Number(formValues.subJobFunction)
          : null,
        report_on: formValues.reportOn,
      };
      setSubmitData(request);
    } else {
      setSubmitData(null);
    }
  }, [formValues]);

  // Debug log
  useEffect(() => {
    if (apiResponse) {
    }
  }, [apiResponse]);

  // Options Mapping
  const regionOptions =
    livePayRegions?.data?.map(r => ({ label: r.name, value: r.id })) || [];

  const countryOptions = (() => {
    const region = livePayRegions?.data?.find(r => r.id === formValues.region);
    return region?.countries.map(c => ({ label: c.name, value: c.id })) || [];
  })();

  const jobFunctionOptions =
    companyJobFunctionsData?.data?.map(f => ({
      label: f.name,
      value: f.ta_job_function_id,
    })) || [];

  const subJobFunctionOptions =
    companyJobFunctionsData?.data
      ?.find(f => f.ta_job_function_id === Number(formValues.jobFunction))
      ?.job_sub_functions?.map(s => ({
        label: s.name,
        value: s.ta_sub_job_function_id,
      })) || [];

  // ✅ Handle dropdown & input changes with dependency resets
  const handleSelectChange = key => value => {
    setFormValues(prev => {
      const updated = { ...prev, [key]: value };

      if (key === "region") {
        updated.country = ""; // reset child
      }
      if (key === "jobFunction") {
        updated.subJobFunction = ""; // reset child
      }
      if (key === "reportOn") {
        updated.jobFunction = "";
        updated.subJobFunction = "";
        updated.grade = "";
      }

      return updated;
    });
  };

  // Dynamic fields config
  const dynamicFields = [
    {
      label: "Title",
      name: "title",
      type: "text",
      placeholder: "Enter report title...",
    },
    {
      label: "Region",
      name: "region",
      options: getSortedRegionOptions(regionOptions),
    },
    {
      label: "Country",
      name: "country",
      options: getSortedCountry(countryOptions),
    },
    {
      label: "Report On",
      name: "reportOn",
      options: [
        { label: "Aggregate", value: "aggregate" },
        { label: "Payroll Data", value: "payroll_data" },
        { label: "Tom Data", value: "tom_data" },
      ],
    },
    {
      label: "Grade",
      name: "grade",
      options:
        jobGradesData?.data?.map(g => ({
          label: g.grade,
          value: g.id,
        })) || [],
    },
    {
      label: "Company Job Function",
      name: "jobFunction",
      options: jobFunctionOptions,
    },
    {
      label: "Company Job Sub Function",
      name: "subJobFunction",
      options: subJobFunctionOptions,
    },
  ];

  // Salary Chart
  const SalaryChart = ({ title, average, percentiles }) => (
    <div className="mb-16">
      <h3 className="text-xl font-semibold text-themeBlue text-center mb-8">
        {formatString(title)}
      </h3>
      <div className="text-center mb-6">
        <div className="text-sm text-themeInputTitleColor mb-1">
          Average Salary
        </div>
        <div className="text-lg font-medium text-themTextBlack">
          {formatNumberWithCommas(parseInt(average))}
        </div>
      </div>

      <div className="relative max-w-2xl mx-auto">
        <div className="w-full flex items-center">
          {[
            "h-1 bg-blue-200",
            "h-2 bg-blue-300",
            "h-3 bg-blue-600",
            "h-2 bg-blue-500",
            "h-1 bg-blue-300",
          ].flatMap((cls, idx) => [
            <div key={`sep-${idx}`} className="w-px h-5 bg-blue-300"></div>,
            <div
              key={`bar-${idx}`}
              className={`flex-1 rounded-sm ${cls}`}
            ></div>,
          ])}
          <div className="w-px h-4 bg-blue-300"></div>
        </div>

        <div
          className="absolute top-1/2 w-5 h-5 bg-themeOrange rounded-full z-40"
          style={{
            left: (() => {
              const p25 = percentiles?.p25;
              const p50 = percentiles?.p50;
              const p75 = percentiles?.p75;

              if (!p25 || !p50 || !p75) return "50%";

              // Normalize to median scale ONLY
              let percent;

              if (average <= p50) {
                // Average is below median → map to left half (0% → 50%)
                percent = ((average - p25) / (p50 - p25)) * 50;
              } else {
                // Average is above median → map to right half (50% → 100%)
                percent = 50 + ((average - p50) / (p75 - p50)) * 50;
              }

              // Clamp within entire 0–100% center section
              percent = Math.max(0, Math.min(percent, 100));

              return percent + "%";
            })(),
            transform: "translate(-50%, -50%)",
          }}
        />
      </div>

      <div className="percentilesMap flex justify-between gap-1 items-center py-2 max-w-2xl mx-auto">
        {Object.entries(percentiles).map(([key, val]) => (
          <div key={key}>
            <div className="title font-semibold text-xs text-center">
              {key === "p25"
                ? "25th Percentile"
                : key === "p50"
                ? "Median"
                : "75th Percentile"}
            </div>
            <div className="value text-xs text-center">
              {formatNumberWithCommas(parseInt(val))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <div className="bg-themeCore">
        <div className="jobCreationBar bg-white px-4 py-6 rounded-lg flex justify-between items-center">
          <h1 className="title text-themeBlue font-medium text-3xl">
            Benchmarking By Function
          </h1>
        </div>
      </div>

      <main className="min-h-screen bg-white p-8 mt-5 rounded-lg">
        <div className="max-w-6xl mx-auto">
          {/* Dynamic fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {dynamicFields.map(field =>
              field.type === "text" ? (
                <div key={field.name}>
                  <label className="block mb-2 font-semibold text-lg text-themeBlue">
                    {field.label}
                  </label>
                  <input
                    type="text"
                    placeholder={field.placeholder}
                    value={formValues[field.name] || ""}
                    onChange={e =>
                      handleSelectChange(field.name)(e.target.value)
                    }
                    className="w-full border text-lg px-4 py-2 mt-1 rounded-md"
                  />
                </div>
              ) : (
                <CustomDropdown
                  key={field.name}
                  label={field.label}
                  value={formValues[field.name]}
                  onChange={handleSelectChange(field.name)}
                  options={field.options}
                  placeholder={field.label}
                />
              )
            )}
          </div>

          {/* Data Visualization Section */}
          <section className="rounded-lg shadow-sm p-8">
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-12">
              Data Visualization
            </h2>

            {/* <SalaryChart title={"test"} average={1577212} percentiles={{p25:1265062,p50:1561495,p75:2024934}} /> */}

            {!isValidSubmitData && (
              <p className="text-center text-gray-400">
                Please fill out all the fields above to see the benchmarking
                report.
              </p>
            )}

            {isLoading && (
              <p className="text-center text-gray-500">Loading report...</p>
            )}

            {error && (
              <p className="text-center text-red-500">
                {error?.message ||
                  "Something went wrong while fetching the report."}
              </p>
            )}

            {isValidSubmitData &&
              apiResponse &&
              !isLoading &&
              !error &&
              (() => {
                const offerData =
                  apiResponse?.offer_data || apiResponse?.data?.offer_data;

                if (offerData && Object.keys(offerData).length > 0) {
                  return Object.entries(offerData).map(([key, value]) => (
                    <SalaryChart key={key} title={key} {...value} />
                  ));
                }

                return (
                  <p className="text-center text-gray-400">
                    No salary data available for the selected filters.
                  </p>
                );
              })()}
          </section>
        </div>
      </main>
    </>
  );
};

export default BenchmarkingByTitle;
